import React, { useEffect, useState } from "react";
import { Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import api from "../../utils/api";
import "./AssistantKnowledge.css";
import { API_BASE_URL } from '../../utils/apiBase';
import { confirmDialog } from '../../components/notifications/notificationService';

const emptyForm = {
  title: "",
  module: "",
  question: "",
  answer: "",
  path: "",
  menuPath: "",
  keywords: "",
  requiredPrivileges: "",
  isActive: true
};

const splitList = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);

const AssistantKnowledge = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/assistant/admin/knowledge`);
      setItems(data);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to load AI knowledge");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const editItem = (item) => {
    setEditingId(item._id);
    setForm({
      title: item.title || "",
      module: item.module || "",
      question: item.question || "",
      answer: item.answer || "",
      path: item.path || "",
      menuPath: (item.menuPath || []).join(", "),
      keywords: (item.keywords || []).join(", "),
      requiredPrivileges: (item.requiredPrivileges || []).join(", "),
      isActive: item.isActive !== false
    });
  };

  const saveItem = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      ...form,
      menuPath: splitList(form.menuPath),
      keywords: splitList(form.keywords),
      requiredPrivileges: splitList(form.requiredPrivileges)
    };

    try {
      if (editingId) {
        await api.put(`${API_BASE_URL}/api/assistant/admin/knowledge/${editingId}`, payload);
      } else {
        await api.post(`${API_BASE_URL}/api/assistant/admin/knowledge`, payload);
      }
      resetForm();
      loadItems();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save AI knowledge");
    } finally {
      setSaving(false);
    }
  };

  const disableItem = async (id) => {
    if (!await confirmDialog({ title: 'Disable AI knowledge?', message: 'This item will no longer be available to the assistant.', confirmText: 'Disable' })) return;
    try {
      await api.delete(`${API_BASE_URL}/api/assistant/admin/knowledge/${id}`);
      loadItems();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to disable AI knowledge");
    }
  };

  return (
    <div className="assistant-admin-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`assistant-admin-content ${collapsed ? "collapsed" : ""}`}>
        <div className="assistant-admin-sticky-header">
          <Header />
        </div>

        <div className="assistant-admin-scroll-area">
          <div className="assistant-admin-page">
            <header className="assistant-admin-header">
        <div>
          <h1>AI Knowledge</h1>
          <p>Maintain FAQs, help content, route hints, and permission-aware training data for the CRM assistant.</p>
        </div>
        <button type="button" onClick={loadItems} disabled={loading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      <main className="assistant-admin-layout">
        <form className="assistant-admin-form" onSubmit={saveItem}>
          <div className="assistant-form-title">
            <h2>{editingId ? "Edit Knowledge" : "Add Knowledge"}</h2>
            <button type="button" onClick={resetForm}>
              <Plus size={16} />
              New
            </button>
          </div>

          <label>
            Title
            <input name="title" value={form.title} onChange={updateField} required placeholder="Add Endorsement" />
          </label>

          <label>
            Module
            <input name="module" value={form.module} onChange={updateField} placeholder="Manage Policy" />
          </label>

          <label>
            Question
            <input name="question" value={form.question} onChange={updateField} placeholder="Where can I add endorsement?" />
          </label>

          <label>
            Answer
            <textarea name="answer" value={form.answer} onChange={updateField} required rows={5} placeholder="Explain the workflow or field guidance." />
          </label>

          <div className="assistant-form-grid">
            <label>
              Route
              <input name="path" value={form.path} onChange={updateField} placeholder="/endorsements/add" />
            </label>
            <label>
              Menu Path
              <input name="menuPath" value={form.menuPath} onChange={updateField} placeholder="Manage Policy, Endorsement" />
            </label>
          </div>

          <label>
            Keywords
            <input name="keywords" value={form.keywords} onChange={updateField} placeholder="endorsement, modify policy, document" />
          </label>

          <label>
            Required Privileges
            <input name="requiredPrivileges" value={form.requiredPrivileges} onChange={updateField} placeholder="managePolicy.endorsements.management.view" />
          </label>

          <label className="assistant-checkbox">
            <input type="checkbox" name="isActive" checked={form.isActive} onChange={updateField} />
            Active
          </label>

          <button type="submit" className="assistant-save-button" disabled={saving}>
            <Save size={16} />
            {saving ? "Saving..." : "Save Knowledge"}
          </button>
        </form>

        <section className="assistant-admin-list">
          {loading ? (
            <div className="assistant-empty-state">Loading knowledge...</div>
          ) : items.length === 0 ? (
            <div className="assistant-empty-state">No custom AI knowledge has been added yet.</div>
          ) : (
            items.map((item) => (
              <article className={`assistant-knowledge-row ${item.isActive ? "" : "is-disabled"}`} key={item._id}>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.answer}</p>
                  <span>{[item.module, item.path].filter(Boolean).join(" | ")}</span>
                </div>
                <div className="assistant-row-actions">
                  <button type="button" onClick={() => editItem(item)}>Edit</button>
                  <button type="button" onClick={() => disableItem(item._id)} aria-label="Disable knowledge item">
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            ))
          )}
        </section>
      </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantKnowledge;
